// src/screens/Feedback.jsx
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import "../css/Complaints.css";
import "../css/Dashboard.css";
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

// ====== ENV CONFIG ======
const FEEDBACK_API = (process.env.REACT_APP_FEEDBACK_API_URL || "").trim();
const FEEDBACK_SUMMARY_API = (
  process.env.REACT_APP_FEEDBACK_SUMMARY_API_URL ||
  (FEEDBACK_API ? `${FEEDBACK_API.replace(/\/+$/, "")}/summary` : "")
).trim();

// Reports APIs
const REPORT_COMMENTS_API = (
  process.env.REACT_APP_FEEDBACK_REPORT_COMMENTS_API_URL || ""
).trim();
const REPORT_REPLIES_API = (
  process.env.REACT_APP_FEEDBACK_REPORT_REPLIES_API_URL || ""
).trim();
const REPORT_ACTION_BASE = (
  process.env.REACT_APP_FEEDBACK_REPORT_ACTION_BASE_URL || ""
).trim();

// Contact Messages API
// REACT_APP_CONTACT_MESSAGES_API_URL=https://backend.tabdhey.bt/admin/api/contact-messages
const CONTACT_MESSAGES_API = (
  process.env.REACT_APP_CONTACT_MESSAGES_API_URL || ""
).trim();

// ── Reply APIs ──
// REACT_APP_REPLY_SMS_API_URL=https://backend.tabdhey.bt/sms/api/sms/send
// REACT_APP_REPLY_SMS_API_KEY=ne_master_9c8b7a6d5e4f3a2b1c0d9e8f7a6b5c4d   ← x-api-key for SMS
// REACT_APP_REPLY_EMAIL_API_URL=https://backend.tabdhey.bt/admin/api/contact-messages/send-email
const REPLY_SMS_API = (process.env.REACT_APP_REPLY_SMS_API_URL || "").trim();
const REPLY_SMS_API_KEY = (process.env.REACT_APP_REPLY_SMS_API_KEY || "").trim();
const REPLY_EMAIL_API = (process.env.REACT_APP_REPLY_EMAIL_API_URL || "").trim();

const ROLE_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "merchant", label: "Merchants" },
  { value: "driver", label: "Drivers" },
  { value: "user", label: "Users" },
];

const ROW_LIMIT = 20;

/* =================== SMALL HELPERS =================== */

const toISODate = (d) => (d || "").slice(0, 10);
const rangeLabel = (s, e) => (s || e ? `${s || "…"} to ${e || "…"}` : "All");

const TZ = "Asia/Thimphu";

const nowThimphuStamp = () => {
  const date = new Date();
  const dPart = date.toLocaleDateString("en-CA", { timeZone: TZ });
  const tPart = date.toLocaleTimeString("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${dPart} ${tPart}`;
};

function formatEpochMs(ms) {
  if (ms == null) return "-";
  const n = Number(ms);
  if (!Number.isFinite(n)) return "-";
  try {
    const dt = new Date(n);
    const d = dt.toLocaleDateString("en-CA", { timeZone: TZ });
    const t = dt.toLocaleTimeString("en-GB", {
      timeZone: TZ,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${d} ${t}`;
  } catch {
    return "-";
  }
}

function formatDateTimeStr(str) {
  if (!str) return "-";
  try {
    const dt = new Date(str);
    if (isNaN(dt.getTime())) return str;
    const d = dt.toLocaleDateString("en-CA", { timeZone: TZ });
    const t = dt.toLocaleTimeString("en-GB", {
      timeZone: TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${d} ${t}`;
  } catch {
    return str;
  }
}

function buildRangeURL(base, start, end, role) {
  const url = new URL(base, window.location.origin);
  const s = toISODate(start);
  const e = toISODate(end);
  if (s) url.searchParams.set("start_date", s);
  if (e) url.searchParams.set("end_date", e);
  if (role && role !== "all") url.searchParams.set("role", role);
  if (s && !url.searchParams.has("from")) url.searchParams.set("from", s);
  if (e && !url.searchParams.has("to")) url.searchParams.set("to", e);
  return url;
}

function mapFromServer(raw) {
  const tsRaw =
    (raw.created_at || raw.createdAt || raw.date_time || raw.datetime || raw.date || "") + "";
  const ts = tsRaw.trim();
  let date_only = "";
  let time_only = "";
  if (ts) {
    const normalized = ts.replace("T", " ");
    const parts = normalized.split(" ");
    date_only = parts[0] || "";
    time_only = (parts[1] || "").slice(0, 5);
  }
  const roleRaw = raw.role ?? raw.user_role ?? "";
  const role = String(roleRaw).toLowerCase();
  return {
    id: raw.id ?? null,
    user_id: String(raw.user_id ?? raw.userId ?? raw.user ?? raw.uid ?? ""),
    rating: Number(raw.rating ?? raw.score ?? raw.stars ?? 0),
    comment: raw.comment ?? raw.feedback ?? "",
    platform: raw.platform ?? raw.device_platform ?? raw.platform_type ?? "",
    os_version: raw.os_version ?? raw.osVersion ?? raw.os ?? "",
    app_version: raw.app_version ?? raw.appVersion ?? "",
    device_model: raw.device_model ?? raw.deviceModel ?? "",
    network_type: raw.network_type ?? raw.networkType ?? raw.network ?? "",
    role,
    created_at: ts,
    date_only,
    time_only,
  };
}

// Map contact message — field names match the actual API response:
// { id, full_name, contact_type, contact_value, user_type, message, status, created_at, updated_at }
function mapContactMessage(raw) {
  return {
    id: raw.id ?? raw._id ?? null,
    name: raw.full_name ?? raw.name ?? raw.fullName ?? "",
    contact: raw.contact_value ?? raw.contact ?? raw.email ?? raw.phone ?? "",
    contact_method: raw.contact_type ?? raw.contact_method ?? raw.contactMethod ?? raw.type ?? "",
    role: raw.user_type ?? raw.role ?? raw.user_role ?? "",
    message: raw.message ?? raw.body ?? raw.content ?? "",
    created_at: raw.created_at ?? raw.createdAt ?? raw.date ?? "",
    updated_at: raw.updated_at ?? raw.updatedAt ?? null,
    status: raw.status ?? "new",
  };
}

const prettyRole = (role) => {
  if (!role) return "-";
  return role.charAt(0).toUpperCase() + role.slice(1);
};

const formatDateTime = (f) => {
  const d = f.date_only || "";
  const t = f.time_only || "";
  if (!d && !t) return "-";
  return t ? `${d} ${t}` : d;
};

function filterFeedback(list, searchTerm, startDate, endDate, roleFilter) {
  const term = (searchTerm || "").toLowerCase();
  return list.filter((f) => {
    if (startDate && f.date_only && f.date_only < startDate) return false;
    if (endDate && f.date_only && f.date_only > endDate) return false;
    if (roleFilter !== "all" && f.role !== roleFilter) return false;
    const haystack = [
      f.user_id, f.rating, f.comment, f.platform, f.os_version,
      f.app_version, f.device_model, f.network_type, f.role,
      f.date_only, f.time_only,
    ].join(" ").toLowerCase();
    return haystack.includes(term);
  });
}

function getAdminUserId() {
  try {
    const explicit = localStorage.getItem("admin_id");
    if (explicit) return explicit;
    const uid = localStorage.getItem("user_id");
    if (uid) return uid;
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      const id = parsed?.user_id ?? parsed?.id ?? parsed?._id;
      if (id != null) return String(id);
    }
  } catch { }
  return null;
}

function getStoredAccessToken() {
  try {
    const directToken =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("accessToken") ||
      sessionStorage.getItem("authToken");

    if (directToken) return String(directToken).trim();

    const rawUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (rawUser) {
      const user = JSON.parse(rawUser);
      return String(
        user?.access_token ||
        user?.accessToken ||
        user?.token ||
        user?.adminToken ||
        user?.authToken ||
        user?.jwt ||
        ""
      ).trim();
    }
  } catch { }
  return "";
}

async function fetchJsonWithTimeout(url, opts = {}, timeoutMs = 15000) {
  const controller = opts.signal ? null : new AbortController();
  const signal = opts.signal || controller?.signal;
  const t = setTimeout(() => { try { controller?.abort(); } catch { } }, timeoutMs);

  try {
    const res = await fetch(url, { ...opts, signal, cache: "no-store" });
    let json = null;

    try {
      json = await res.json();
    } catch {
      json = null;
    }

    if (!res.ok) {
      const msg = json?.message || json?.error || `Request failed (HTTP ${res.status})`;

      if (res.status === 401) {
        throw new Error(`Unauthorized (401): Invalid/expired token. Please login again and try. (${msg})`);
      }

      if (res.status === 403) {
        throw new Error(`Forbidden (403): Your token has no permission for this endpoint. (${msg})`);
      }

      throw new Error(msg);
    }

    return json;
  } catch (e) {
    const raw = String(e?.message || e);

    if (/failed to fetch/i.test(raw) || e?.name === "TypeError") {
      throw new Error(`Failed to fetch (network/CORS). URL: ${url}\n\nIf Postman works but browser fails, backend must enable CORS for this endpoint and allow the Authorization header.`);
    }

    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function fetchOkWithTimeout(url, opts = {}, timeoutMs = 15000) {
  const controller = opts.signal ? null : new AbortController();
  const signal = opts.signal || controller?.signal;
  const t = setTimeout(() => { try { controller?.abort(); } catch { } }, timeoutMs);
  try {
    const res = await fetch(url, { ...opts, signal, cache: "no-store" });
    if (!res.ok) {
      let msg = `Request failed (HTTP ${res.status})`;
      try { const j = await res.json(); msg = j?.message || j?.error || msg; } catch {
        try { const text = await res.text(); if (text) msg = text.slice(0, 160); } catch { }
      }
      if (res.status === 401) throw new Error(`Unauthorized (401): Invalid/expired token. Please login again and try. (${msg})`);
      if (res.status === 403) throw new Error(`Forbidden (403): Your token has no permission for this endpoint. (${msg})`);
      throw new Error(msg);
    }
    return true;
  } catch (e) {
    const raw = String(e?.message || e);
    if (/failed to fetch/i.test(raw) || e?.name === "TypeError") {
      throw new Error(`Failed to fetch (network/CORS). URL: ${url}\n\nIf Postman works but browser fails, backend must enable CORS for this endpoint.`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

// ====================== COMPONENT ======================
export default function Feedback() {
  useIdleLogout();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [allFeedback, setAllFeedback] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [reportType, setReportType] = useState("food");
  const [reportTarget, setReportTarget] = useState("comment");
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [reports, setReports] = useState([]);
  const [actingReportId, setActingReportId] = useState(null);

  // ── Contact Messages state ──
  const [contactMessages, setContactMessages] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [contactExporting, setContactExporting] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [replyingId, setReplyingId] = useState(null);

  const startRef = useRef(null);
  const endRef = useRef(null);

  const getAuthHeaders = useCallback((withJson = false) => {
    const token = getStoredAccessToken();
    const headers = { Accept: "application/json" };

    if (withJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, []);

  /* ===================== FEEDBACK LOAD ===================== */

  const fetchFeedback = useCallback(async () => {
    if (!FEEDBACK_API) { setError("Feedback API URL is not configured."); setAllFeedback([]); return; }
    try {
      setLoading(true); setError("");
      const urlObj = buildRangeURL(FEEDBACK_API, startDate, endDate, roleFilter);
      const json = await fetchJsonWithTimeout(urlObj.toString(), { headers: getAuthHeaders(false) }, 15000);
      const items = Array.isArray(json) ? json : json.data || [];
      // Sort by created_at descending (latest first)
      const sortedItems = items.sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || a.date_time || a.datetime || a.date || 0);
        const dateB = new Date(b.created_at || b.createdAt || b.date_time || b.datetime || b.date || 0);
        return dateB - dateA;
      });
      setAllFeedback(sortedItems.map(mapFromServer));
    } catch (e) {
      setError(e.message || "Failed to load feedback."); setAllFeedback([]);
    } finally { setLoading(false); }
  }, [getAuthHeaders, startDate, endDate, roleFilter]);

  const fetchSummary = useCallback(async () => {
    if (!FEEDBACK_SUMMARY_API) { setSummary(null); return; }
    try {
      setSummaryLoading(true);
      const urlObj = buildRangeURL(FEEDBACK_SUMMARY_API, startDate, endDate, roleFilter);
      const json = await fetchJsonWithTimeout(urlObj.toString(), { headers: getAuthHeaders(false) }, 15000);
      const data = json.data || json.summary || json;
      setSummary({
        total_ratings: Number(data?.total_ratings ?? data?.total ?? 0),
        avg_rating: Number(data?.avg_rating ?? data?.average ?? 0),
        breakdown: Array.isArray(data?.breakdown) ? data.breakdown : [],
      });
    } catch (e) { setSummary(null); }
    finally { setSummaryLoading(false); }
  }, [getAuthHeaders, startDate, endDate, roleFilter]);

  useEffect(() => { fetchFeedback(); fetchSummary(); }, [fetchFeedback, fetchSummary]);

  /* ===================== REPORTED CONTENT LOAD ===================== */

  const mapReport = useCallback((r) => ({
    report_id: r?.report_id ?? null,
    type: r?.type ?? reportType,
    target: r?.target ?? reportTarget,
    rating_id: r?.rating_id ?? null,
    reply_id: r?.reply_id ?? null,
    business_id: r?.business_id ?? null,
    reporter_user_id: r?.reporter_user_id ?? null,
    reported_user_id: r?.reported_user_id ?? null,
    reason: r?.reason ?? "-",
    reported_text: r?.reported_text ?? "-",
    created_at: r?.created_at ?? null,
    status: r?.status ?? "-",
    reporter: r?.reporter ?? {},
    reported_user: r?.reported_user ?? {},
    raw: r,
  }), [reportType, reportTarget]);

  const fetchReports = useCallback(async () => {
    setReportsError("");
    const base = reportTarget === "comment" ? REPORT_COMMENTS_API : REPORT_REPLIES_API;
    if (!base) { setReports([]); setReportsError("Reports API URL is not configured."); return; }
    const token = getStoredAccessToken();
    if (!token) { setReports([]); setReportsError("Missing access token. Please login again."); return; }
    try {
      setReportsLoading(true);
      const url = new URL(base, window.location.origin);
      url.searchParams.set("type", reportType);
      const json = await fetchJsonWithTimeout(url.toString(), { headers: getAuthHeaders(false) }, 15000);
      const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      // Sort by created_at descending (latest first)
      const sortedArr = arr.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      setReports(sortedArr.map(mapReport));
    } catch (e) { setReports([]); setReportsError(e.message || "Failed to load reports."); }
    finally { setReportsLoading(false); }
  }, [getAuthHeaders, reportTarget, reportType, mapReport]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  /* ===================== CONTACT MESSAGES LOAD ===================== */

  const fetchContactMessages = useCallback(async () => {
    if (!CONTACT_MESSAGES_API) { setContactMessages([]); return; }
    try {
      setContactLoading(true); setContactError("");
      const json = await fetchJsonWithTimeout(CONTACT_MESSAGES_API, { headers: getAuthHeaders(false) }, 15000);
      const items = Array.isArray(json) ? json : json.data || json.messages || [];
      // Sort by created_at descending (latest first)
      const sortedItems = items.sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || a.date || 0);
        const dateB = new Date(b.created_at || b.createdAt || b.date || 0);
        return dateB - dateA;
      });
      setContactMessages(sortedItems.map(mapContactMessage));
    } catch (e) { setContactError(e.message || "Failed to load contact messages."); setContactMessages([]); }
    finally { setContactLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchContactMessages(); }, [fetchContactMessages]);

  const fetchContactMessageById = useCallback(async (msgId) => {
    if (!CONTACT_MESSAGES_API || !msgId) return null;

    const base = CONTACT_MESSAGES_API.replace(/\/+$/, "");
    const json = await fetchJsonWithTimeout(
      `${base}/${encodeURIComponent(msgId)}`,
      { headers: getAuthHeaders(false) },
      15000
    );

    let raw = json?.data ?? json?.contact_message ?? json?.contactMessage ?? json;
    if (Array.isArray(raw)) raw = raw[0];
    if (!raw || typeof raw !== "object") return null;

    const mapped = mapContactMessage(raw);

    setContactMessages((prev) =>
      prev.map((m) => (m.id === mapped.id ? { ...m, ...mapped } : m))
    );

    return mapped;
  }, [getAuthHeaders]);

  const filteredContacts = useMemo(() => {
    const term = contactSearch.toLowerCase();
    let filtered = contactMessages;
    if (term) {
      filtered = contactMessages.filter((m) =>
        [m.name, m.contact, m.contact_method, m.role, m.message, m.status]
          .join(" ").toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [contactMessages, contactSearch]);

  /* ── Internal PATCH helper — used by dropdown AND reply flow ── */
  const patchStatus = useCallback(async (msgId, newStatus) => {
    if (!CONTACT_MESSAGES_API || !msgId) return;

    try {
      const base = CONTACT_MESSAGES_API.replace(/\/+$/, "");

      await fetchOkWithTimeout(
        `${base}/${encodeURIComponent(msgId)}/status`,
        {
          method: "PATCH",
          headers: getAuthHeaders(true),
          body: JSON.stringify({ status: newStatus }),
        },
        15000
      );

      setContactMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, status: newStatus } : m))
      );
    } catch (e) {
      // Non-fatal — do not block the reply success flow
      console.warn("[Feedback] patchStatus failed:", e.message);
    }
  }, [getAuthHeaders]);

  /* ── Status dropdown change ── */
  const handleUpdateStatus = async (msg, newStatus) => {
    if (!CONTACT_MESSAGES_API) return;
    if (!msg.id) { Swal.fire("Error", "Missing message ID.", "error"); return; }
    try {
      setUpdatingStatusId(msg.id);
      await patchStatus(msg.id, newStatus);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  /* ── REPLY to contact message ──
     SMS:   POST https://backend.tabdhey.bt/sms/api/sms/send
            headers: { x-api-key: <REPLY_SMS_API_KEY> }
            body:    { to, text, from }

     Email: POST https://backend.tabdhey.bt/admin/api/contact-messages/send-email
            headers: { Authorization: Bearer <access_token> }
            body:    { to, subject, message }

     Channel auto-selected from contact_type (phone → SMS, email → Email).
     Status auto-patched to "replied" after a successful send.
  ── */
  const handleReply = async (messageRow) => {
    let msg = messageRow;

    // Covers GET /api/contact-messages/:id with Authorization header before replying.
    // If the detail endpoint fails, we still continue with the row data already loaded.
    try {
      if (messageRow?.id) {
        const latestMessage = await fetchContactMessageById(messageRow.id);
        if (latestMessage) msg = latestMessage;
      }
    } catch (e) {
      console.warn("[Feedback] fetchContactMessageById failed:", e.message);
    }

    const contactMethod = (msg.contact_method || "email").toLowerCase();
    const isPhone = contactMethod === "phone";
    const defaultChannel = isPhone ? "sms" : "email";

    const canSMS = !!REPLY_SMS_API;
    const canEmail = !!REPLY_EMAIL_API;

    if (!canSMS && !canEmail) {
      Swal.fire(
        "Missing API",
        "Neither REACT_APP_REPLY_SMS_API_URL nor REACT_APP_REPLY_EMAIL_API_URL is configured in your admin .env file.",
        "error"
      );
      return;
    }

    const senderName = msg.name || "the user";
    const originalMsg = msg.message || "";
    const showEmail = canEmail;
    const showSMS = canSMS;
    const lockedChannel = (!showEmail && showSMS) ? "sms" : (!showSMS && showEmail) ? "email" : null;
    const startChannel = lockedChannel || defaultChannel;

    const result = await Swal.fire({
      title: `Reply to ${senderName}`,
      html: `
        <style>
          .rm-body { text-align: left; }
          .rm-original {
            background: rgba(0,0,0,.04); border-left: 3px solid #0d6efd;
            border-radius: 6px; padding: 10px 14px; margin-bottom: 16px;
            font-size: 12px; color: #555; max-height: 70px; overflow-y: auto;
            white-space: pre-wrap; word-break: break-word;
          }
          .rm-label { display: block; font-weight: 600; font-size: 12px; margin-bottom: 5px; color: #374151; }
          .rm-ch-row { display: flex; gap: 8px; margin-bottom: 14px; }
          .rm-ch-btn {
            flex: 1; padding: 7px; border-radius: 8px; border: 1.5px solid #d1d5db;
            background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
            transition: all .15s ease; color: #374151;
          }
          .rm-ch-btn.sel { border-color: #0d6efd; background: #0d6efd14; color: #0d6efd; }
          .rm-ch-btn:hover { border-color: #0d6efd; }
          .rm-ch-btn:disabled { opacity: .45; cursor: not-allowed; }
          .rm-input {
            width: 100%; margin-bottom: 10px; padding: 8px 12px;
            border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 13px;
            box-sizing: border-box; font-family: inherit;
          }
          .rm-textarea {
            width: 100%; padding: 8px 12px; border: 1.5px solid #d1d5db;
            border-radius: 8px; font-size: 13px; resize: vertical;
            min-height: 100px; box-sizing: border-box; font-family: inherit;
          }
          .rm-input:focus, .rm-textarea:focus {
            outline: none; border-color: #0d6efd;
            box-shadow: 0 0 0 3px rgba(13,110,253,.12);
          }
          .rm-contact-tag {
            display: inline-block; background: #0d6efd14; color: #0d6efd;
            border-radius: 6px; padding: 2px 8px; font-size: 11px;
            font-weight: 600; margin-bottom: 14px;
          }
        </style>
        <div class="rm-body">
          <div class="rm-original">
            <strong>Original:</strong> ${originalMsg || "—"}
          </div>
          <span class="rm-contact-tag">
            📬 ${msg.contact || "No contact"} · ${(msg.contact_method || "email").toUpperCase()}
          </span>
          <span class="rm-label">Send via</span>
          <div class="rm-ch-row">
            <button type="button" id="ch-email"
              class="rm-ch-btn${startChannel === "email" ? " sel" : ""}"
              ${!showEmail ? "disabled" : ""}
              onclick="
                document.getElementById('ch-email').classList.add('sel');
                document.getElementById('ch-sms').classList.remove('sel');
                document.getElementById('rm-ch-val').value='email';
                document.getElementById('rm-subject-row').style.display='block';
              ">✉️ Email</button>
            <button type="button" id="ch-sms"
              class="rm-ch-btn${startChannel === "sms" ? " sel" : ""}"
              ${!showSMS ? "disabled" : ""}
              onclick="
                document.getElementById('ch-sms').classList.add('sel');
                document.getElementById('ch-email').classList.remove('sel');
                document.getElementById('rm-ch-val').value='sms';
                document.getElementById('rm-subject-row').style.display='none';
              ">💬 SMS</button>
          </div>
          <input type="hidden" id="rm-ch-val" value="${startChannel}" />

          <!-- Subject — only relevant for email -->
          <div id="rm-subject-row" style="display:${startChannel === "sms" ? "none" : "block"}">
            <span class="rm-label">Subject</span>
            <input id="rm-subject" class="rm-input"
              placeholder="e.g. Response from TabDhey Support"
              value="Response from TabDhey Support" />
          </div>

          <span class="rm-label" style="margin-top:8px;">Message</span>
          <textarea id="rm-message" class="rm-textarea"
            placeholder="Write your reply here…">Hello ${senderName},

Thank you for reaching out to us.

Best regards,
TabDhey Support Team</textarea>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Send Reply",
      cancelButtonText: "Cancel",
      width: 520,
      preConfirm: () => {
        const channel = document.getElementById("rm-ch-val")?.value || startChannel;
        const subject = (document.getElementById("rm-subject")?.value || "").trim();
        const message = (document.getElementById("rm-message")?.value || "").trim();
        if (channel === "email" && !subject) { Swal.showValidationMessage("Subject is required for email"); return null; }
        if (!message) { Swal.showValidationMessage("Message is required"); return null; }
        return { channel, subject, message };
      },
    });

    if (!result.isConfirmed || !result.value) return;

    const { channel, subject, message } = result.value;

    try {
      setReplyingId(msg.id);
      const token = getStoredAccessToken();

      if (channel === "sms") {
        // POST https://backend.tabdhey.bt/sms/api/sms/send
        // Requires x-api-key header — does NOT use the admin Bearer token
        const smsHeaders = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
        // Add x-api-key if configured
        if (REPLY_SMS_API_KEY) smsHeaders["x-api-key"] = REPLY_SMS_API_KEY;

        const res = await fetch(REPLY_SMS_API, {
          method: "POST",
          headers: smsHeaders,
          body: JSON.stringify({
            to: msg.contact,   // phone number the user submitted
            text: message,
            from: "TabDhey",
          }),
        });
        if (!res.ok) {
          let errMsg = `HTTP ${res.status}`;
          try { const j = await res.json(); errMsg = j?.message || j?.error || errMsg; } catch { }
          throw new Error(errMsg);
        }
      } else {
        // POST https://backend.tabdhey.bt/admin/api/contact-messages/send-email
        // Uses the standard admin Bearer token
        const emailHeaders = {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const res = await fetch(REPLY_EMAIL_API, {
          method: "POST",
          headers: emailHeaders,
          body: JSON.stringify({
            to: msg.contact,   // email address the user submitted
            subject,
            message,
          }),
        });
        if (!res.ok) {
          let errMsg = `HTTP ${res.status}`;
          try { const j = await res.json(); errMsg = j?.message || j?.error || errMsg; } catch { }
          throw new Error(errMsg);
        }
      }

      // Auto-mark as replied (non-blocking — failure just logs a warning)
      await patchStatus(msg.id, "replied");

      Swal.fire({
        icon: "success",
        title: "Reply sent!",
        text: `Sent via ${channel === "sms" ? "SMS" : "Email"} • Status updated to Replied.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("Reply failed", e.message || "Unable to send reply.", "error");
    } finally {
      setReplyingId(null);
    }
  };

  /* ── DELETE contact message ── */
  const handleDeleteContact = async (msg) => {
    if (!CONTACT_MESSAGES_API) { Swal.fire("Missing API", "Contact Messages API URL is not configured.", "error"); return; }
    if (!msg.id) { Swal.fire("Error", "Missing message ID.", "error"); return; }
    const confirmed = await Swal.fire({
      icon: "warning", title: "Delete this message?",
      text: `From: ${msg.name || "Unknown"}`,
      showCancelButton: true, confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;
    try {
      setDeletingContactId(msg.id);
      const base = CONTACT_MESSAGES_API.replace(/\/+$/, "");
      await fetchOkWithTimeout(
        `${base}/${encodeURIComponent(msg.id)}`,
        { method: "DELETE", headers: getAuthHeaders(false) },
        15000
      );
      await Swal.fire({ icon: "success", title: "Message deleted", timer: 1200, showConfirmButton: false });
      await fetchContactMessages();
    } catch (e) {
      Swal.fire("Delete failed", e.message || "Unable to delete.", "error");
    } finally { setDeletingContactId(null); }
  };

  /* ── EXPORT contact messages to Excel (EXPORTS ALL DATA, NOT JUST VISIBLE ROWS) ── */
  const exportContactToExcel = async () => {
    try {
      setContactExporting(true);
      const title = `Contact Messages  Exported at: (${nowThimphuStamp()})`;
      const stamp = `Total: ${contactMessages.length} messages`;

      // Headers array
      const headers = [
        "Sl.No",
        "Full Name",
        "Contact",
        "Method",
        "Role",
        "Message",
        "Status",
        "Received At"
      ];

      // Prepare data rows
      const dataRows = contactMessages.map((m, i) => [
        i + 1,
        m.name || "-",
        m.contact || "-",
        (m.contact_method || "-").toUpperCase(),
        m.role || "-",
        m.message || "-",
        (m.status || "-").charAt(0).toUpperCase() + (m.status || "-").slice(1),
        formatDateTimeStr(m.created_at),
      ]);

      // Combine everything: title row, stamp row, empty row, headers, then data
      const allRows = [
        [title],           // Row 1: Title
        [stamp],           // Row 2: Stamp
        [],                // Row 3: Empty row
        headers,           // Row 4: Headers
        ...dataRows        // Row 5+: Data
      ];

      // Create worksheet from the combined rows
      const ws = XLSX.utils.aoa_to_sheet(allRows);

      // Set column widths
      ws['!cols'] = [
        { wch: 8 },   // Sl.No
        { wch: 25 },  // Full Name
        { wch: 20 },  // Contact
        { wch: 10 },  // Method
        { wch: 15 },  // Role
        { wch: 50 },  // Message
        { wch: 12 },  // Status
        { wch: 20 },  // Received At
      ];

      // Merge title cells (row 0, columns 0-7)
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },  // Title spans all columns
        { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } }   // Stamp spans all columns
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contact Messages");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `contact-messages-${Date.now()}.xlsx`);
    } catch (e) {
      Swal.fire("Export failed", e.message || "Unable to export.", "error");
    } finally { setContactExporting(false); }
  };

  /* ===================== FILTERING + METRICS ===================== */

  const filtered = useMemo(
    () => filterFeedback(allFeedback, searchTerm, startDate, endDate, roleFilter),
    [allFeedback, searchTerm, startDate, endDate, roleFilter]
  );

  const sumRating = filtered.reduce((acc, f) => acc + (Number(f.rating) || 0), 0);
  const avgFromSummary = summary && Number.isFinite(summary.avg_rating) ? summary.avg_rating : null;
  const avgRating = avgFromSummary != null ? avgFromSummary : filtered.length ? sumRating / filtered.length : 0;
  const totalFromSummary = summary && Number.isFinite(summary.total_ratings) ? summary.total_ratings : null;
  const totalFeedback = totalFromSummary != null ? totalFromSummary : filtered.length;
  const positive = filtered.filter((f) => Number(f.rating) >= 4).length;
  const critical = filtered.filter((f) => Number(f.rating) <= 2).length;

  /* ===================== CHART DATA ===================== */

  const ratingLabels = [1, 2, 3, 4, 5];
  const breakdownMap = new Map();
  (summary?.breakdown || []).forEach((b) => {
    const r = Number(b.rating);
    const c = Number(b.count || b.total || 0);
    if (Number.isFinite(r) && Number.isFinite(c)) breakdownMap.set(r, c);
  });
  const ratingCounts = ratingLabels.map((star) =>
    breakdownMap.has(star) ? breakdownMap.get(star) : filtered.filter((f) => Number(f.rating) === star).length
  );
  const ratingData = {
    labels: ratingLabels.map((star) => `${star} ★`),
    datasets: [{ data: ratingCounts, backgroundColor: ["#dc3545", "#fd7e14", "#ffc107", "#0d6efd", "#198754"] }],
  };
  const platforms = Array.from(new Set(filtered.map((f) => f.platform || "Unknown")));
  const platformData = {
    labels: platforms,
    datasets: [{
      data: platforms.map((p) => filtered.filter((f) => (f.platform || "Unknown") === p).length),
      backgroundColor: ["#0d6efd", "#6c757d", "#20c997", "#ffc107", "#dc3545", "#6610f2"],
    }],
  };
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { boxWidth: 12, padding: 8, font: { size: 11 } } } },
  };
  // Limit to 20 rows for display (latest first since data is already sorted)
  const pageRows = filtered.slice(0, ROW_LIMIT);
  // Limit reports to 20 rows for display
  const displayedReports = reports.slice(0, ROW_LIMIT);
  // Limit contact messages to 20 rows for display
  const displayedContacts = filteredContacts.slice(0, ROW_LIMIT);

  /* ===================== DELETE FEEDBACK ===================== */

  const handleDelete = async (row) => {
    if (!FEEDBACK_API) { Swal.fire("Missing API", "Feedback API URL is not configured.", "error"); return; }
    if (!row.id) { Swal.fire("Error", "Missing feedback ID.", "error"); return; }
    const adminId = getAdminUserId();
    if (!adminId) { Swal.fire("Missing admin ID", "Unable to determine the logged-in admin user.", "error"); return; }
    const confirmed = await Swal.fire({
      icon: "warning", title: "Delete this feedback?",
      text: `User ID: ${row.user_id}, Rating: ${row.rating}`,
      showCancelButton: true, confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;
    try {
      setDeletingId(row.id);
      const base = FEEDBACK_API.replace(/\/+$/, "");
      const res = await fetch(`${base}/${encodeURIComponent(row.id)}`, {
        method: "DELETE", headers: getAuthHeaders(true),
        body: JSON.stringify({ admin_user_id: Number(adminId) || adminId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await Swal.fire({ icon: "success", title: "Feedback deleted", timer: 1200, showConfirmButton: false });
      await fetchFeedback(); await fetchSummary();
    } catch (e) {
      Swal.fire("Delete failed", e.message || "Unable to delete.", "error");
    } finally { setDeletingId(null); }
  };

  /* ===================== EXPORT FEEDBACK EXCEL (EXPORTS ALL DATA, NOT JUST VISIBLE ROWS) ===================== */

  const exportToExcel = async () => {
    if (!FEEDBACK_API) { Swal.fire("Missing API", "Feedback API URL is not configured.", "error"); return; }
    try {
      setExporting(true);
      const urlObj = buildRangeURL(FEEDBACK_API, startDate, endDate, roleFilter);
      if (!urlObj.searchParams.has("pageSize")) urlObj.searchParams.set("pageSize", "5000");
      const json = await fetchJsonWithTimeout(urlObj.toString(), { headers: getAuthHeaders(false) }, 20000);
      const items = Array.isArray(json) ? json : json.data || [];
      const exportList = filterFeedback(items.map(mapFromServer), searchTerm, startDate, endDate, roleFilter);
      const title = `System Feedback Record (${rangeLabel(startDate, endDate)})`;
      const stamp = `Exported at: ${nowThimphuStamp()}`;

      // Headers array
      const headers = [
        "Sl.No",
        "User ID",
        "Rating",
        "Comment",
        "Platform",
        "OS Version",
        "App Version",
        "Device Model",
        "Network Type",
        "Role",
        "Date & Time"
      ];

      // Prepare data rows
      const dataRows = exportList.map((f, i) => [
        i + 1,
        f.user_id || "-",
        f.rating || "-",
        f.comment || "-",
        f.platform || "-",
        f.os_version || "-",
        f.app_version || "-",
        f.device_model || "-",
        f.network_type || "-",
        prettyRole(f.role),
        formatDateTime(f),
      ]);

      // Combine everything: title row, stamp row, empty row, headers, then data
      const allRows = [
        [title],           // Row 1: Title
        [stamp],           // Row 2: Stamp
        [],                // Row 3: Empty row
        headers,           // Row 4: Headers
        ...dataRows        // Row 5+: Data
      ];

      // Create worksheet from the combined rows
      const ws = XLSX.utils.aoa_to_sheet(allRows);

      // Set column widths
      ws['!cols'] = [
        { wch: 8 },   // Sl.No
        { wch: 15 },  // User ID
        { wch: 8 },   // Rating
        { wch: 40 },  // Comment
        { wch: 12 },  // Platform
        { wch: 12 },  // OS Version
        { wch: 12 },  // App Version
        { wch: 15 },  // Device Model
        { wch: 12 },  // Network Type
        { wch: 12 },  // Role
        { wch: 18 },  // Date & Time
      ];

      // Merge title cells (row 0, columns 0-10)
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },  // Title spans all columns
        { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } }   // Stamp spans all columns
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Feedback");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `feedback-${startDate || "start"}_${endDate || "end"}-${Date.now()}.xlsx`);
    } catch (e) {
      Swal.fire("Export failed", e.message || "Unable to export Excel.", "error");
    } finally { setExporting(false); }
  };

  /* ===================== REPORT ACTIONS ===================== */

  const requireReportBase = () => {
    if (!REPORT_ACTION_BASE) { Swal.fire("Missing API", "REACT_APP_FEEDBACK_REPORT_ACTION_BASE_URL is not configured in .env", "error"); return false; }
    return true;
  };

  const doIgnoreReport = async (reportId) => {
    if (!requireReportBase()) return;
    const confirmed = await Swal.fire({
      icon: "question", title: "Ignore this report?",
      text: "This keeps the comment/reply but removes it from OPEN reports list.",
      showCancelButton: true, confirmButtonText: "Yes, ignore", cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;
    try {
      setActingReportId(reportId);
      const base = REPORT_ACTION_BASE.replace(/\/+$/, "");
      await fetchOkWithTimeout(`${base}/${encodeURIComponent(reportId)}/ignore`, { method: "POST", headers: getAuthHeaders(false) }, 15000);
      Swal.fire({ icon: "success", title: "Report ignored", timer: 1100, showConfirmButton: false });
      await fetchReports();
    } catch (e) { Swal.fire("Action failed", e.message || "Unable to ignore report.", "error"); }
    finally { setActingReportId(null); }
  };

  const doDeleteReported = async (reportId) => {
    if (!requireReportBase()) return;
    const isComment = reportTarget === "comment";
    const confirmed = await Swal.fire({
      icon: "warning",
      title: isComment ? "Delete the reported COMMENT?" : "Delete the reported REPLY?",
      text: "This will delete the reported content using report_id.",
      showCancelButton: true, confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;
    try {
      setActingReportId(reportId);
      const base = REPORT_ACTION_BASE.replace(/\/+$/, "");
      await fetchOkWithTimeout(`${base}/${encodeURIComponent(reportId)}/${isComment ? "comment" : "reply"}`, { method: "DELETE", headers: getAuthHeaders(false) }, 15000);
      Swal.fire({ icon: "success", title: "Deleted", timer: 1100, showConfirmButton: false });
      await fetchReports();
    } catch (e) { Swal.fire("Delete failed", e.message || "Unable to delete reported content.", "error"); }
    finally { setActingReportId(null); }
  };

  /* ===================== DATE PICKER HELPER ===================== */

  const openPickerSafely = (ref) => (e) => {
    e.preventDefault();
    try { ref.current?.showPicker?.(); } catch { }
  };

  /* ====================== RENDER ====================== */

  const isActiveComment = reportTarget === "comment";
  const isActiveReply = reportTarget === "reply";
  const canReply = !!(REPLY_SMS_API || REPLY_EMAIL_API);

  return (
    <>
      <style>{`
        .report-pill {
          border-radius: 999px !important; padding: 8px 16px !important;
          min-height: 38px !important; line-height: 22px !important;
          width: fit-content !important; white-space: nowrap !important;
          border: 1px solid rgba(13,110,253,.18) !important;
          transition: transform .12s ease, box-shadow .12s ease, background-color .12s ease, color .12s ease, border-color .12s ease;
        }
        .report-pill.is-active { background: #0d6efd !important; color: #fff !important; border-color: transparent !important; box-shadow: 0 8px 18px rgba(13,110,253,.25); }
        .report-pill:not(.is-active) { background: rgba(13,110,253,.06) !important; color: #0d6efd !important; }
        .report-pill:hover { transform: translateY(-1px); }
        .report-pill.is-active:hover { background: #0b5ed7 !important; color: #fff !important; }
        .report-pill:not(.is-active):hover { background: rgba(13,110,253,.12) !important; color: #0d6efd !important; border-color: rgba(13,110,253,.28) !important; }
        td.report-actions-cell { min-width: 190px; }
        .report-actions { display: inline-flex; gap: 10px; align-items: center; justify-content: flex-start; flex-wrap: nowrap; isolation: isolate; }
        .report-action-btn {
          appearance: none; border: 1px solid rgba(15,23,42,.18); background: rgba(255,255,255,.96);
          color: rgba(15,23,42,.92); font-weight: 700; font-size: 12px; letter-spacing: .2px;
          padding: 7px 12px; min-width: 78px; border-radius: 12px; line-height: 1.1;
          white-space: nowrap; cursor: pointer; opacity: 1 !important; filter: none !important;
          backdrop-filter: none !important; -webkit-backdrop-filter: none !important;
          box-shadow: 0 6px 14px rgba(2,6,23,.10);
          transition: transform .12s ease, box-shadow .12s ease, background-color .12s ease, border-color .12s ease, color .12s ease;
          position: relative; z-index: 2;
        }
        .report-action-btn:hover { transform: translateY(-1px); box-shadow: 0 10px 20px rgba(2,6,23,.14); }
        .report-action-btn:active { transform: translateY(0); box-shadow: 0 6px 14px rgba(2,6,23,.10); }
        .report-action-btn:disabled { opacity: .55 !important; cursor: not-allowed; transform: none; box-shadow: none; }
        .report-action-btn.is-ignore { border-color: rgba(13,110,253,.25); color: #0d6efd; background: rgba(13,110,253,.07); }
        .report-action-btn.is-ignore:hover { background: rgba(13,110,253,.12); border-color: rgba(13,110,253,.35); }
        .report-action-btn.is-delete { border-color: rgba(220,53,69,.25); color: #dc3545; background: rgba(220,53,69,.07); }
        .report-action-btn.is-delete:hover { background: rgba(220,53,69,.12); border-color: rgba(220,53,69,.35); }

        /* ── Contact Messages ── */
        .contact-section-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 14px; }
        .contact-section-header h4 { margin: 0; }
        .contact-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .contact-search { position: relative; }
        .contact-search input { padding: 6px 12px 6px 32px; border: 1px solid rgba(0,0,0,.15); border-radius: 8px; font-size: 13px; min-width: 200px; }
        .contact-search .search-ico { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); opacity: .45; font-size: 13px; }
        .contact-badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 600; white-space: nowrap; }
        .contact-badge-new     { background: #0d6efd22; color: #0d6efd; border: 1px solid #0d6efd44; }
        .contact-badge-read    { background: #19875422; color: #198754; border: 1px solid #19875444; }
        .contact-badge-replied { background: #6610f222; color: #6610f2; border: 1px solid #6610f244; }
        .contact-badge-archived{ background: #6c757d22; color: #6c757d; border: 1px solid #6c757d44; }
        .msg-cell { max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .status-select { border: 1px solid rgba(0,0,0,.15); border-radius: 8px; font-size: 12px; padding: 3px 8px; background: transparent; cursor: pointer; max-width: 110px; }
        .status-select:disabled { opacity: .55; cursor: not-allowed; }
        .contact-actions { display: inline-flex; gap: 6px; align-items: center; }
        .btn-contact-reply {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 8px; border: none;
          background: rgba(13,110,253,.1); color: #0d6efd; cursor: pointer;
          font-size: 15px; transition: background .15s ease, transform .12s ease; flex-shrink: 0;
        }
        .btn-contact-reply:hover:not(:disabled) { background: rgba(13,110,253,.2); transform: translateY(-1px); }
        .btn-contact-reply:disabled { opacity: .4; cursor: not-allowed; }
        .btn-contact-reply.is-replied { background: rgba(102,16,242,.1); color: #6610f2; }
        .btn-contact-reply.is-replied:hover:not(:disabled) { background: rgba(102,16,242,.2); }
      `}</style>

      {/* ===================== FEEDBACK SECTION ===================== */}

      <div className="toolbar">
        <div className="search-container">
          <i className="bi bi-search search-icon" />
          <input type="text" placeholder="Search feedback…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="role-select-wrapper">
          <select id="feedbackRoleSelect" className="form-select form-select-sm feedback-role-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            {ROLE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
          </select>
        </div>
        <div className="date-container">
          <input type="date" ref={startRef} value={startDate} onChange={(e) => setStartDate(e.target.value)} onMouseDown={openPickerSafely(startRef)} onPointerDown={openPickerSafely(startRef)} aria-label="Start date" />
          <span className="date-separator">to</span>
          <input type="date" ref={endRef} value={endDate} onChange={(e) => setEndDate(e.target.value)} onMouseDown={openPickerSafely(endRef)} onPointerDown={openPickerSafely(endRef)} aria-label="End date" />
        </div>
        <div className="export-actions ms-auto">
          <button className="export-btn" onClick={exportToExcel} disabled={exporting}>{exporting ? "Exporting…" : "Export Excel"}</button>
        </div>
      </div>

      {error && (<div className="alert alert-danger my-2 py-2"><small>{error}</small></div>)}

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4">
        {[
          { label: "Total Feedback", value: summaryLoading ? "…" : totalFeedback },
          { label: "Average Rating", value: summaryLoading ? "…" : avgRating.toFixed(2) },
          { label: "4–5 ★ Ratings", value: positive },
          { label: "1–2 ★ Ratings", value: critical },
        ].map((m, i) => (
          <div className="col" key={i}>
            <div className="card h-100">
              <div className="card-body text-center">
                <h6 className="text-secondary fw-medium">{m.label}</h6>
                <p className="display-6 fw-semibold mb-0">{m.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row gx-4 gy-4 mb-4">
        <div className="col-md-6"><div className="card chart-card h-100 p-3"><h5>Rating Distribution</h5><Doughnut data={ratingData} options={chartOpts} /></div></div>
        <div className="col-md-6"><div className="card chart-card h-100 p-3"><h5>By Platform</h5><Doughnut data={platformData} options={chartOpts} /></div></div>
      </div>

      <h4 className="mb-2">System Feedback Record</h4>
      <div className="card complaints-table-card mb-4">
        <div className="table-responsive">
          <table className="table table-borderless table-striped align-middle mb-0">
            <thead>
              <tr>
                <th>Sl.No</th><th>User ID</th><th>Rating</th><th>Comment</th>
                <th>Platform</th><th>OS Version</th><th>App Version</th>
                <th>Device Model</th><th>Network Type</th><th>Role</th>
                <th>Date &amp; Time</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-4">Loading…</td></tr>
              ) : pageRows.length ? (
                pageRows.map((f, i) => (
                  <tr key={f.id ?? i}>
                    <td>{i + 1}</td>
                    <td>{f.user_id || "-"}</td>
                    <td>{f.rating || "-"}</td>
                    <td>{f.comment || "-"}</td>
                    <td>{f.platform || "-"}</td>
                    <td>{f.os_version || "-"}</td>
                    <td>{f.app_version || "-"}</td>
                    <td>{f.device_model || "-"}</td>
                    <td>{f.network_type || "-"}</td>
                    <td>{prettyRole(f.role)}</td>
                    <td>{formatDateTime(f)}</td>
                    <td>
                      <button type="button" className="btn btn-delete" onClick={() => handleDelete(f)} disabled={deletingId === f.id} title="Delete feedback">
                        {deletingId === f.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-trash-fill" />}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={12} className="text-center py-4">No feedback records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > ROW_LIMIT && (
          <div className="px-3 py-2 border-top">
            <small className="text-muted">
              Showing {ROW_LIMIT} of {filtered.length} records (latest first). Export Excel to see all data.
            </small>
          </div>
        )}
      </div>

      {/* ===================== REPORTED SECTION ===================== */}
      <h4 className="mb-2">Reported Feedback</h4>
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <button type="button" className={`report-pill ${isActiveComment ? "is-active" : ""}`} onClick={() => setReportTarget("comment")}>Reported Comments</button>
            <button type="button" className={`report-pill ${isActiveReply ? "is-active" : ""}`} onClick={() => setReportTarget("reply")}>Reported Replies</button>
            <select className="form-select form-select-sm" style={{ width: 160, borderRadius: 999, minHeight: 38, paddingInline: 14 }} value={reportType} onChange={(e) => setReportType(e.target.value)} aria-label="Report type">
              <option value="food">Food</option>
              <option value="mart">Mart</option>
            </select>
          </div>
          {reportsError && (<div className="alert alert-warning mt-3 mb-0 py-2"><small>{reportsError}</small></div>)}
          <div className="card complaints-table-card mb-0">
            <div className="table-responsive">
              <table className="table table-borderless table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Report ID</th><th style={{ width: 90 }}>Status</th>
                    <th style={{ width: 180 }}>Created</th><th>Reason</th><th>Reported Text</th>
                    <th style={{ width: 220 }}>Reporter</th><th style={{ width: 220 }}>Reported User</th>
                    <th style={{ width: 170 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportsLoading ? (
                    <tr><td colSpan={8} className="text-center py-4">Loading reports…</td></tr>
                  ) : displayedReports.length ? (
                    displayedReports.map((r) => {
                      const rep = r.reporter || {}; const bad = r.reported_user || {}; const busy = actingReportId === r.report_id;
                      return (
                        <tr key={r.report_id}>
                          <td className="fw-semibold">{r.report_id ?? "-"}</td>
                          <td><span className={`badge ${String(r.status).toLowerCase() === "open" ? "bg-warning text-dark" : "bg-secondary"}`}>{r.status || "-"}</span></td>
                          <td>{formatEpochMs(r.created_at)}</td>
                          <td>{r.reason || "-"}</td>
                          <td style={{ maxWidth: 380 }}><div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.reported_text || ""}>{r.reported_text || "-"}</div></td>
                          <td><div className="fw-semibold">{rep.user_name || "-"}</div><small className="text-muted">ID: {rep.user_id ?? "-"}{rep.phone ? ` • ${rep.phone}` : ""}</small></td>
                          <td><div className="fw-semibold">{bad.user_name || "-"}</div><small className="text-muted">ID: {bad.user_id ?? "-"}{bad.role ? ` • ${bad.role}` : ""}</small></td>
                          <td className="report-actions-cell">
                            <div className="report-actions">
                              <button type="button" className="report-action-btn is-ignore" disabled={busy} onClick={() => doIgnoreReport(r.report_id)}>{busy ? "..." : "Ignore"}</button>
                              <button type="button" className="report-action-btn is-delete" disabled={busy} onClick={() => doDeleteReported(r.report_id)}>{busy ? "..." : "Delete"}</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan={8} className="text-center py-4 text-muted">No {reportTarget === "comment" ? "reported comments" : "reported replies"} found for <b>{reportType}</b>.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {reports.length > ROW_LIMIT && (
            <div className="px-3 py-2 border-top mt-2">
              <small className="text-muted">
                Showing {ROW_LIMIT} of {reports.length} reports (latest first). Export to see all data.
              </small>
            </div>
          )}
        </div>
      </div>

      {/* ===================== CONTACT MESSAGES SECTION ===================== */}
      <div className="contact-section-header">
        <h4 className="mb-0">Contact Messages from Website</h4>
        <div className="contact-toolbar">
          <div className="contact-search">
            <i className="bi bi-search search-ico" />
            <input type="text" placeholder="Search messages…" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
          </div>
          <button className="export-btn" onClick={exportContactToExcel} disabled={contactExporting || contactMessages.length === 0}>
            {contactExporting ? "Exporting…" : "Export Excel"}
          </button>
        </div>
      </div>

      {contactError && (<div className="alert alert-warning mb-3 py-2"><small>{contactError}</small></div>)}

      {!canReply && CONTACT_MESSAGES_API && (
        <div className="alert alert-info mb-3 py-2">
          <small>
            <strong>Reply disabled:</strong> Add{" "}
            <code>REACT_APP_REPLY_SMS_API_URL</code> and/or{" "}
            <code>REACT_APP_REPLY_EMAIL_API_URL</code> to your admin <code>.env</code> to enable the reply button.
          </small>
        </div>
      )}

      <div className="card complaints-table-card mb-4">
        <div className="table-responsive">
          <table className="table table-borderless table-striped align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 55 }}>Sl.No</th>
                <th style={{ width: 150 }}>Full Name</th>
                <th style={{ width: 185 }}>Contact</th>
                <th style={{ width: 75 }}>Method</th>
                <th style={{ width: 165 }}>Role</th>
                <th>Message</th>
                <th style={{ width: 125 }}>Status</th>
                <th style={{ width: 148 }}>Received At</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contactLoading ? (
                <tr><td colSpan={9} className="text-center py-4">Loading messages…</td></tr>
              ) : !CONTACT_MESSAGES_API ? (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-muted">
                    <small>Set <code>REACT_APP_CONTACT_MESSAGES_API_URL</code> in your .env to enable this table.</small>
                  </td>
                </tr>
              ) : displayedContacts.length ? (
                displayedContacts.map((m, i) => {
                  const isBusy = updatingStatusId === m.id || deletingContactId === m.id || replyingId === m.id;
                  const isReplied = m.status === "replied";
                  return (
                    <tr key={m.id ?? i}>
                      <td>{i + 1}</td>
                      <td className="fw-semibold">{m.name || "-"}</td>
                      <td>{m.contact || "-"}</td>
                      <td>
                        <span className="badge bg-secondary" style={{ textTransform: "capitalize" }}>
                          {m.contact_method || "-"}
                        </span>
                      </td>
                      <td>{m.role || "-"}</td>
                      <td><div className="msg-cell" title={m.message || ""}>{m.message || "-"}</div></td>
                      <td>
                        <select
                          className="status-select"
                          value={m.status || "new"}
                          disabled={isBusy}
                          onChange={(e) => handleUpdateStatus(m, e.target.value)}
                          title="Update status"
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                        </select>
                        {updatingStatusId === m.id && (<span className="spinner-border spinner-border-sm ms-1" />)}
                      </td>
                      <td>{formatDateTimeStr(m.created_at)}</td>
                      <td>
                        <div className="contact-actions">
                          <button
                            type="button"
                            className={`btn-contact-reply${isReplied ? " is-replied" : ""}`}
                            onClick={() => handleReply(m)}
                            disabled={isBusy || !canReply}
                            title={
                              !canReply
                                ? "Set REACT_APP_REPLY_SMS_API_URL / REACT_APP_REPLY_EMAIL_API_URL in .env to enable"
                                : isReplied ? "Send another reply" : "Reply to this message"
                            }
                          >
                            {replyingId === m.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : <i className={`bi ${isReplied ? "bi-reply-all-fill" : "bi-reply-fill"}`} />
                            }
                          </button>
                          <button
                            type="button"
                            className="btn btn-delete"
                            onClick={() => handleDeleteContact(m)}
                            disabled={isBusy}
                            title="Delete message"
                          >
                            {deletingContactId === m.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : <i className="bi bi-trash-fill" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={9} className="text-center py-4 text-muted">No contact messages found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredContacts.length > ROW_LIMIT && (
          <div className="px-3 py-2 border-top">
            <small className="text-muted">
              Showing {ROW_LIMIT} of {filteredContacts.length} message{filteredContacts.length !== 1 ? "s" : ""}
              {contactSearch ? ` matching "${contactSearch}"` : ""} (latest first). Export Excel to see all data.
            </small>
          </div>
        )}
        {filteredContacts.length > 0 && filteredContacts.length <= ROW_LIMIT && (
          <div className="px-3 py-2 border-top">
            <small className="text-muted">
              Showing {filteredContacts.length} message{filteredContacts.length !== 1 ? "s" : ""}
              {contactSearch ? ` matching "${contactSearch}"` : ""} (latest first)
            </small>
          </div>
        )}
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </>
  );
}